import { Component, OnInit } from '@angular/core';
import {
  DashboardEngineiusKPIComponent,
  LiveFeedOptionEnum,
  MetricDisplayStyle,
} from './dashboard-engineius-kpi.component';
import _ from 'lodash';
import { KPILivefeed, TaskTypeInformationDTO } from '../../../interfaces/kpis/kpi-livefeed';
import { SelectItem } from 'primeng';
import { ChartDataset } from 'src/app/interfaces/kpis/customer-kpi-totals';

// this enum is used to index the totalsChartData array (different for each kpi component)
// for example, completedMoves = index 0 of chart data array
enum ChartMetric {
  CompletedMoves,
  TargetMoves,
  PerfectMoves,
  TasksPerActiveSupplier,
  LessThan1HourLate,
  OnTime,
  Turndowns,
  FullDataCapture,
  NonManualIntervention,
}

@Component({
  selector: 'dashboard-engineius-general-kpi',
  styleUrls: ['./dashboard-engineius-kpi.component.scss'],
  templateUrl: './dashboard-engineius-kpi.component.html',
})
export class DashboardEngineiusKPIGeneralComponent extends DashboardEngineiusKPIComponent {
  liveFeedData: KPILivefeed[];
  liveFeedLabels: string[];

  volumeMetrics: any[];

  liveFeedOptions: SelectItem[];
  selectedLiveFeed: LiveFeedOptionEnum = LiveFeedOptionEnum.Daily;

  dailyLiveFeedOptions: SelectItem[];
  selectedDailyLiveFeedOption: number = 1;

  weeklyLiveFeedOptions: SelectItem[];
  selectedWeeklyLiveFeedOption: number = 1;

  monthlyLiveFeedOptions: SelectItem[];
  selectedMonthlyLiveFeedOption: number = 1;

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.liveFeedOptions = [
      { label: 'Daily', value: LiveFeedOptionEnum.Daily },
      { label: 'Weekly', value: LiveFeedOptionEnum.Weekly },
      { label: 'Monthly', value: LiveFeedOptionEnum.Monthly },
    ];
    this.dailyLiveFeedOptions = [
      { label: '1 Day', value: 1 },
      { label: '7 Days', value: 7 },
      { label: '14 Days', value: 14 },
      { label: '28 Days', value: 28 },
    ];

    this.weeklyLiveFeedOptions = [];
    for (let i = 1; i <= 12; i++) {
      this.weeklyLiveFeedOptions.push({ label: `${i} week${i > 1 ? 's' : ''}`, value: i });
    }

    this.monthlyLiveFeedOptions = [{ label: 'This Month', value: 1 }];

    this.monthlyLiveFeedOptions = [{ label: 'This Month', value: 1 }];
    await this.loadData();
  }

  async loadData() {
    super.loadData();

    const promise1 = this.kpiService.getKPIMonthlyTotals(
      this.kpiStartDate,
      this.selectedMonthRange,
    );
    const promise2 = this.loadLiveFeedData();

    try {
      const [monthlyTotals, liveFeedData] = await Promise.all([promise1, promise2]);
      this.monthlyTotals = monthlyTotals;
      // Handle liveFeedData if needed
    } catch (error) {
      // Handle errors
      console.log('Error occured while loading monthlyTotals:', error);
    }

    //console.log("** MONTHLY TOTALS: ", this.monthlyTotals);

    this.setMonthDates(this.selectedMonthRange);

    this.completedTotals = this.monthlyTotals.filter((x) => !x.isCancelled);
    this.cancelledTotals = this.monthlyTotals.filter((x) => x.isCancelled);

    this.totalsChartData = [];

    const dataMap = new Map<number, number[]>([
      [
        ChartMetric.CompletedMoves,
        this.completedTotals.map((x) => {
          return x.totalCompleted;
        }),
      ],
      [
        ChartMetric.TargetMoves,
        this.completedTotals.map((x) => {
          return x.targetCompleted;
        }),
      ],
      [
        ChartMetric.PerfectMoves,
        this.completedTotals.map((x) => {
          return x.perfectMoves;
        }),
      ],
      [
        ChartMetric.TasksPerActiveSupplier,
        this.completedTotals.map((x) => {
          return x.tasksPerActiveSupplier;
        }),
      ],
      [
        ChartMetric.LessThan1HourLate,
        this.completedTotals.map((x) => {
          return x.lessThan1HourLate;
        }),
      ],
      [
        ChartMetric.OnTime,
        this.completedTotals.map((x) => {
          return x.onTime;
        }),
      ],
      [
        ChartMetric.Turndowns,
        this.cancelledTotals.map((x) => {
          return x.turndowns;
        }),
      ],
      [
        ChartMetric.FullDataCapture,
        this.completedTotals.map((x) => {
          return x.movementsWithFullDataCapture;
        }),
      ],
      [
        ChartMetric.NonManualIntervention,
        this.completedTotals.map((x) => {
          return x.nonManualIntervention;
        }),
      ],
    ]);

    this.totalsChartData = Array.from(dataMap.values());
    this.targetDataIndex = <number>ChartMetric.TargetMoves;

    // create metric card data for each kpi measure
    this.monthlyMetrics = [];
    this.setVolumeMetrics();
    //this.setEngagementMetrics();
    this.setServiceLevelMetrics();
    this.setEconomicsAndEfficiencyMetrics();

    this.isLoading = false;

    console.log('General Metrics: ', this.monthlyMetrics);
  }

  async loadLiveFeedData() {
    let range = 1;
    switch (this.selectedLiveFeed) {
      case LiveFeedOptionEnum.Daily:
        range = this.selectedDailyLiveFeedOption;
        break;
      case LiveFeedOptionEnum.Weekly:
        range = this.selectedWeeklyLiveFeedOption;
        break;
      case LiveFeedOptionEnum.Monthly:
        range = this.selectedMonthlyLiveFeedOption;
        break;
    }

    this.liveFeedData = await this.kpiService.getLiveFeedData(
      this.kpiStartDate,
      range,
      this.selectedLiveFeed,
    );

    //console.log("Live feed data: ", this.liveFeedData);

    // create an item in the label array for every date
    this.liveFeedLabels = this.liveFeedData.map((x) => {
      const completed = x.taskTypeInformations.find((t) => t.label === 'Completed');
      return `${x.dateLabel} - ${completed?.data ?? 0}`;
    });
  }

  async refreshLiveFeedData(isWeekly: boolean) {
    this.isLoadingLiveFeed = true;
    // this.isWeeklyLiveFeed = isWeekly;
    await this.loadLiveFeedData();

    this.setLiveFeedMetric();
    this.isLoadingLiveFeed = false;
  }

  setVolumeMetrics() {
    this.volumeMetrics = [];
    let metric = this.getMetricData_v2(
      'Completed Moves',
      ChartMetric.CompletedMoves,
      false,
      false,
      false,
      MetricDisplayStyle.Number,
      true,
    );
    let chartData = this.getCompletedMovesChartData(
      ChartMetric.CompletedMoves,
      ChartMetric.TargetMoves,
    );
    metric.chartData = chartData;
    metric.chartData.options = this.baseChartOptions;
    metric.totalName = 'Total';
    metric.noComp = true;
    metric.showTarget = true;
    metric.tooltip = 'Completed tasks in the period (updated nightly)';

    this.volumeMetrics.push(metric);
    this.setLiveFeedMetric();

    this.monthlyMetrics.push({ groupName: 'Volume', metrics: this.volumeMetrics });
  }

  setLiveFeedMetric() {
    // live view has no metrics only chart data
    let chartData = this.getLiveFeedChart();

    let metric = {
      title: 'Live Completed Moves',
      chartData,
      totalName: 'Total',
      percAgainstComplete: false,
      showPercentageAsHeadline: false,
      liveFeed: true,
      tooltip: 'Completed tasks updated live, categorised by move type',
    };

    const options = _.cloneDeep(this.stackedChartOptions);
    options.scales.yAxes[0].stacked = true;

    const maxStackedSum = Math.max(
      0,
      ...this.liveFeedData.map((feed) =>
        feed.taskTypeInformations
          .filter((t) => !t.useLine)
          .reduce((sum, t) => sum + (t.data || 0), 0),
      ),
    );

    (options.scales.yAxes[0].ticks as any).suggestedMax = maxStackedSum;

    metric.chartData.options = options;

    let completedMap = this.liveFeedData.map((x) => x.taskTypeInformations[0]?.data ?? 0);
    let total = 0;
    if (completedMap.length > 0) {
      total = completedMap.reduce((x, y) => x + y);
    }

    chartData.total = total;

    if (this.volumeMetrics.length == 1) this.volumeMetrics.push(metric);
    else this.volumeMetrics[1] = metric;
  }

  setEngagementMetrics() {
    let metrics = [];

    // let metric = this.getMetricData_v2("Perfect Moves", ChartMetric.PerfectMoves, true, true, false, MetricDisplayStyle.Number);
    // let chartData = this.getStackedChartData(ChartMetric.PerfectMoves, ChartMetric.CompletedMoves, 'rgba(10, 220, 85, 0.5)'
    //   , 'rgba(66, 164, 245, 0.5)', "Perfect Moves", "Completed Moves", "bar", false);
    //
    // metric.totalName = "Perfect Moves";
    // metric.chartData = chartData;
    // metric.tooltip = "Number of 'Perfect Jobs' as a percentage of total tasks. It is bought off the mobile portal. 'Perfect Job' = 'At system job price','Completed on time' and 'With no negative customer feedback'";
    //
    // metrics.push(metric);

    let metric = this.getMetricData_v2(
      'Tasks Per Active Supplier',
      ChartMetric.TasksPerActiveSupplier,
      false,
      false,
      false,
      MetricDisplayStyle.Number,
    );
    let chartData2 = this.getSupplierTasksChartData(
      ChartMetric.TasksPerActiveSupplier,
      ChartMetric.CompletedMoves,
    );
    metric.totalName = 'Tasks Per.';
    metric.chartData = chartData2;
    metric.chartData.total = 0;
    metric.noPercSymbol = true;
    metric.noCompPerc = true;
    metric.tooltip =
      'Average number of tasks per month for active suppliers ie, where at least one completed tasks within last 65 days';

    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Supplier Engagement', metrics });
  }

  setServiceLevelMetrics() {
    let metrics = [];

    // copy the percentage tooltip chart options and set the formatter to calculate percentages
    let optionsCopy = this.getStackedPercentOptions();

    let metric = this.getMetricData_v2(
      'Less Than 1 Hour Late',
      ChartMetric.LessThan1HourLate,
      true,
      true,
      false,
      MetricDisplayStyle.Number,
    );
    //let chartData = this.getPerformanceChartData(ChartMetric.CompletedMoves, this.totalsChartData[ChartMetric.LessThan1HourLate], true);
    let chartData = this.getStackedChartData(
      ChartMetric.LessThan1HourLate,
      ChartMetric.CompletedMoves,
      'rgba(10, 220, 171, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      '< 1hr Late',
      'Completed Moves',
      'bar',
      false,
    );

    metric.totalName = '< 1 Hour lt.';
    metric.chartData = chartData;
    metric.chartData.options = optionsCopy;
    metric.tooltip =
      'Percentage of movements that arrive with one hour of scheduled SLA time either pick-up, drop off or both';

    metrics.push(metric);

    metric = this.getMetricData_v2(
      'On Time',
      ChartMetric.OnTime,
      true,
      true,
      false,
      MetricDisplayStyle.Number,
    );
    metric.totalName = 'On time';
    // chartData = this.getPerformanceChartData(ChartMetric.CompletedMoves, this.totalsChartData[ChartMetric.OnTime]);
    chartData = this.getStackedChartData(
      ChartMetric.OnTime,
      ChartMetric.CompletedMoves,
      'rgba(20, 222, 94, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'On Time',
      'Completed Moves',
      'bar',
      false,
    );

    metric.chartData = chartData;
    metric.chartData.options = optionsCopy;
    metric.tooltip =
      'Percentage of movements that arrive on scheduled SLA time either pick-up, drop off or both';

    metrics.push(metric);

    let title = 'Turndowns';
    metric = this.getMetricData_v2(
      title,
      ChartMetric.Turndowns,
      true,
      true,
      false,
      MetricDisplayStyle.Number,
    );

    // console.log("Turndown metric: ", metric);

    //let chartData2 = this.getTwoSetChartData(ChartMetric.Turndowns, ChartMetric.CompletedMoves
    //, 'rgba(250, 30, 45, 0.5)', 'rgba(66, 164, 245, 0.5)', title, "Completed Moves");
    chartData = this.getStackedChartData(
      ChartMetric.Turndowns,
      ChartMetric.CompletedMoves,
      'rgba(250, 30, 45, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      title,
      'Completed Moves',
      'bar',
      false,
    );

    metric.totalName = title;
    metric.chartData = chartData;
    metric.chartData.options = optionsCopy;
    metric.tooltip =
      'Cancelled orders with no fees applied, no supplier and not rebooked for future dates as a percentage of total orders';

    console.log('Turndowns chart data: ', metric.chartData);

    metrics.push(metric);

    title = 'Movements with full data capture';
    metric = this.getMetricData_v2(
      title,
      ChartMetric.FullDataCapture,
      true,
      true,
      false,
      MetricDisplayStyle.Number,
    );
    // chartData2 = this.getTwoSetChartData(ChartMetric.FullDataCapture, ChartMetric.CompletedMoves
    //   , 'rgba(10, 220, 85, 0.5)', 'rgba(66, 164, 245, 0.5)', title, "Completed Moves", 'rgba(10, 220, 85, 0.75)');

    chartData = this.getStackedChartData(
      ChartMetric.FullDataCapture,
      ChartMetric.CompletedMoves,
      'rgba(70, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      title,
      'Completed Moves',
      'bar',
      false,
    );

    metric.totalName = 'Moves with data';
    metric.chartData = chartData;
    metric.chartData.options = optionsCopy;
    metric.tooltip =
      'Percentage of tasks with full photos, task tracking (counts GPS points required for journey) and all driver checklist time stamps completed';

    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Service Level', metrics });
  }

  setEconomicsAndEfficiencyMetrics() {
    let metrics = [];

    const filter = (x) => x.nonManualIntervention;
    let title = 'Automated Dispatch';

    // copy the percentage tooltip chart options and set the formatter to calculate percentages
    let optionsCopy = this.getStackedPercentOptions();

    let metric = this.getMetricData_v2(
      title,
      ChartMetric.NonManualIntervention,
      true,
      true,
      false,
      MetricDisplayStyle.Number,
    );

    let chartData = this.getStackedChartData(
      ChartMetric.NonManualIntervention,
      ChartMetric.CompletedMoves,
      'rgba(10, 220, 85, 0.5)',
      'rgba(66, 164, 245, 0.5)',
      'Automated Dispatch',
      'Completed Moves',
      'bar',
      false,
    );

    metric.chartData = chartData;
    metric.chartData.options = optionsCopy;
    metric.totalName = 'Automated Dispatch';
    metric.tooltip =
      'Based on completed jobs in period where we suppliers have purchased jobs at the original BIN price and no outbound call made';

    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Economics and Efficiency', metrics });
  }

  getLiveFeedChart() {
    if (!this.liveFeedData || this.liveFeedData.length < 1) {
      return {
        fill: true,
        datalabels: { display: false },
        borderColor: 'rgba(255,255,255,0.7)',
        borderWidth: 2,
      };
    }

    //Get unique labels from task type
    const kpiLabels = this.liveFeedData[0]?.taskTypeInformations.map((t) => t.label) ?? [];

    //Build dataset
    let datas: ChartDataset[] = kpiLabels
      .map((label) => {
        const first = this.liveFeedData
          .find((feed) => feed.taskTypeInformations.some((t) => t.label === label))
          ?.taskTypeInformations.find((t) => t.label === label)!;

        return {
          label: label,
          data: this.liveFeedData.map((feed) => {
            const entry = feed.taskTypeInformations.find((t) => t.label === label);
            return entry?.data ?? 0;
          }),
          backgroundColor: first.backgroundColor,
          useLine: first.useLine ?? false,
        };
      })
      .filter((ds) => ds.data.some((val) => val !== 0)); //filter out 0 data

    // remove items where all values are 0
    //console.log("Datas: ", datas);
    datas = datas.filter((d) => d.data.length > 0 && d.data.reduce((x, y) => x + y) > 0);
    //Filter out completed
    datas = datas.filter((d) => !d.useLine);
    let datasets = datas.map((data) => {
      return {
        ...data,
        fill: true,
        datalabels: { display: false },
        borderColor: 'rgba(255,255,255,0.7)',
        borderWidth: 2,
      };
    });

    /*
    // set step size according to largest value
    let values = [].concat.apply(
      [],
      datas.map((x) => x.data),
    );
    let maxValue = Math.max(...values);

    let stepSize = Math.max(1, Math.round(maxValue / 10)) * 10;
    let finalStepSize = Math.ceil(stepSize / 10) * 10;
    */

    //console.log("Max value: ", maxValue, " - step size: ", finalStepSize);

    return {
      total: 0,
      chartType: 'bar', // 'doughnut',
      labels: this.liveFeedLabels,
      datasets,
      options: null,
    };
  }

  async liveFeedOptionChanged(event: any) {
    //console.log("Selected live feed type: ", this.selectedLiveFeed, " - event value: ", event.value);

    if (event.value == LiveFeedOptionEnum.Daily) {
      // reset data on live feed chart to daily
      await this.refreshLiveFeedData(false);
    } else {
      // reset data on live feed chart to weekly
      await this.refreshLiveFeedData(true);
    }
  }

  async dailyLiveFeedOptionChanged(event: any) {
    await this.refreshLiveFeedData(false);
  }

  async weeklyLiveFeedOptionChanged($event: any) {
    await this.refreshLiveFeedData(true);
  }
}
